import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { CurrencyFormat } from '@ha/intl';
import { List, ListItem, Typography, units } from '@ha/dsl';
import { useIntl } from 'ha/i18n';
import { getListingPreviewTitle } from 'ha/components.legacy/Listing/helpers';
import { firstAvailableDay as getFirstAvailableDay } from 'ha/utils/exclusionDate/firstAvailableDay';
import { sharpSansBold } from 'ha/constants/fonts/sourceSansHeaders';
import {
  NormalizedAlgoliaListing,
  UtilityBillsValues,
} from 'ha/models/Listing/types';
import { UtilityBillsLabelByValue } from 'ha/models/ListingUtilityBills/translations';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { PropertyType } from '@ha/algolia';
import { getAreaLabel } from 'ha/helpers/unitConverter';

import { getRoomLabel } from '../utils';

interface Props {
  room: NormalizedAlgoliaListing;
  isImperialSystem: boolean;
}

const useStyles = makeStyles()(theme => ({
  infoContainer: {
    padding: units.trbl(0.875, 1, 1, 1),
  },
  listItem: {
    paddingBottom: 0,
  },
  priceContainer: {
    padding: 0,
    alignItems: 'baseline',
    flexWrap: 'wrap',
  },
  priceLabel: {
    color: theme.palette.secondary.dark,
    fontFamily: sharpSansBold.fontFamily,
    fontSize: units.rem(1.25),
  },
  fromLabel: {
    color: theme.palette.secondary.dark,
    fontFamily: sharpSansBold.fontFamily,
    fontSize: units.rem(0.75),
    textTransform: 'uppercase',
    marginRight: '4px',
  },
  billsContainer: {
    fontSize: units.rem(0.75),
    color: theme.palette.grey[600],

    [theme.breakpoints.down('md')]: {
      top: '3px',
    },
  },
  priceInfoDivider: {
    position: 'relative',
    top: '-1px',
    color: theme.palette.grey[500],
    fontWeight: theme.typography.fontWeightBold,
    marginRight: '1px',
    marginLeft: '1px',
    fontSize: units.rem(0.6),
  },
  infoDivider: {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    color: theme.palette.grey[400],
  },
}));

const InfoInternal: React.FC<Props> = ({ room, isImperialSystem }) => {
  const { T, formatDate, formatCurrency } = useIntl();
  const { withDynamicMinimumPrice } = useFeatureFlags();
  const { classes } = useStyles();
  const {
    propertyType,
    bedroomCount,
    currentOccupancy,
    freePlaces,
    bedroomSize,
    exclusions,
    minimalRentalPeriod,
    price,
    currency,
    totalSize,
    priceType,
    minPrice,
  } = room;
  const convertedBedroomSize = useMemo(
    () => getAreaLabel(bedroomSize, isImperialSystem),
    [isImperialSystem, bedroomSize],
  );
  const convertedTotalSize = useMemo(
    () => getAreaLabel(totalSize, isImperialSystem),
    [isImperialSystem, totalSize],
  );

  const listingKindLabel = getListingPreviewTitle(T, propertyType);

  const isPrivateOrSharedRoom =
    propertyType === PropertyType.privateRoom ||
    propertyType === PropertyType.sharedRoom;
  const isStudio = propertyType === PropertyType.studio;
  const isAptSizePresented =
    Boolean(!isPrivateOrSharedRoom && totalSize) ||
    Boolean(isPrivateOrSharedRoom && bedroomSize);

  const firstAvailableDay = getFirstAvailableDay(
    exclusions,
    minimalRentalPeriod,
  );

  const listingDatesLabel =
    firstAvailableDay &&
    `${T('From')} ${formatDate('D MMM YYYY', firstAvailableDay)}`;

  const formattedPrice = Math.floor(price / 100).toString();

  const roomLabel = getRoomLabel(
    T,
    propertyType,
    bedroomCount,
    currentOccupancy,
    freePlaces,
  );

  const currencyFormat =
    currency === 'EUR'
      ? CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION_NO_GROUPING
      : CurrencyFormat.WITH_CURRENCY_CODE_NO_FRACTION_NO_GROUPING;

  return (
    <List className={classes.infoContainer}>
      <ListItem
        disableGutters
        className={classes.priceContainer}
        data-test-locator="Listing Card Price"
      >
        <meta itemProp="price" content={formattedPrice} />
        <meta itemProp="priceCurrency" content={currency} />
        {withDynamicMinimumPrice === 'on' &&
        priceType === 'monthly' &&
        minPrice ? (
          <React.Fragment>
            <span className={classes.fromLabel}>{T('from_price')}</span>
            <Typography.H3 fontWeight="bold" className={classes.priceLabel}>
              {formatCurrency(minPrice * 100, currency, currencyFormat)}
            </Typography.H3>
          </React.Fragment>
        ) : (
          <Typography.H3 fontWeight="bold" className={classes.priceLabel}>
            {formatCurrency(price, currency, currencyFormat)}
          </Typography.H3>
        )}
        <Typography.Caption className={classes.billsContainer}>
          <span className={classes.priceInfoDivider}>/</span>
          {T('Month')} (
          {T(
            UtilityBillsLabelByValue[
              room.utilities || UtilityBillsValues.EXCLUDED
            ],
          )}
          )
        </Typography.Caption>
      </ListItem>

      <ListItem disableGutters className={classes.listItem}>
        <Typography.Caption data-test-locator="Listing Card Main Info">
          {listingKindLabel}

          {roomLabel && !isPrivateOrSharedRoom && !isStudio && (
            <React.Fragment>
              <span className={classes.infoDivider}>•</span>
              {roomLabel}
            </React.Fragment>
          )}

          {isAptSizePresented && (
            <React.Fragment>
              <span className={classes.infoDivider}>•</span>
              {isPrivateOrSharedRoom
                ? convertedBedroomSize
                : convertedTotalSize}
            </React.Fragment>
          )}

          {roomLabel && (isPrivateOrSharedRoom || isStudio) && (
            <React.Fragment>
              <span className={classes.infoDivider}>•</span>
              {roomLabel}
            </React.Fragment>
          )}
        </Typography.Caption>
      </ListItem>

      <ListItem disableGutters className={classes.listItem}>
        <Typography.Caption data-test-locator="Listing Card Additional Info">
          {listingDatesLabel && listingDatesLabel}
        </Typography.Caption>
      </ListItem>
    </List>
  );
};

const Info = React.memo(InfoInternal);
export { Info };
